import React from 'react';

// external libs

import { Navigate, Route, Routes } from 'react-router-dom';

// common

import PrivateRoute from '../components/navigation/PrivateRoute';
import { configs } from '../constants/configs/configs';
import states from '../constants/configs/states';
import { ALL_USER_ROLES } from '../constants/roles/userRoleTypes';
import RouteRenderEnvironment from './RouteRenderEnvironment';

// components

import AccessCodeContainer from '../components/accessCode/AccessCodeContainer';
import ForgotEmailContainer from '../components/anonymous/forgotEmail/ForgotEmailContainer';
import HomeContainer from '../components/anonymous/home/HomeContainer';
import PreOEPLandingContainer from '../components/anonymous/pre-OEP_landing/pre-oep_landingContainer';
import EDESearchAndSelfDesignationContainer from '../components/broker/edeSearchAndDesignation/EDESearchAndSelfDesignationContainer';
import UpcomingDueDateContainer from '../components/charts/UpcomingDueDateContainer';
import SecureInbox from '../components/individualPortal/secureInbox/SecureInbox';
import BrokerConnectContainer from '../containers/BrokerConnectContainer/BrokerConnectContainer';
import IdProofingContainer from '../components/broker/edeSearchAndDesignation/idProofing/IdProofingContainer';
import EDESearchContainer from '../components/broker/edeSearchAndDesignation/search/EDESearchContainer';
import BrokerContainer from '../containers/broker/BrokerContainer';
import CrmContainer from '../containers/crm/CrmContainer';
import DelegationContainer from '../containers/delegation/DelegationContainer';
import DocumentsVerificationContainer from '../containers/documentsVerificationContainer/DocumentsVerificationContainer';
import EntityExportContainer from '../containers/entity/EntityExportContainer';
import EntityTransportBOBContainer from '../containers/entity/EntityTransportBOBContainer';
import FallbackContainer from '../containers/fallback/FallbackContainer';
import CommunicationPreferencesContainer from '../containers/individualPortal/CommunicationPreferencesContainer';
import IndividualPortalContainer from '../containers/individualPortal/IndividualPortalContainer';
import TobaccoContainer from '../containers/individualPortal/tobacco/TobaccoContainer';
import LceContainer from '../containers/lce/LceContainer';
import SsapContainer from '../containers/ssap/SsapContainer';
import TicketMgmtContainer from '../containers/ticketMgmtPortal/TicketMgmtContainer';
import TicketSendMessageContainer from '../containers/ticketMgmtPortal/sendMessage/TicketSendMessageContainer';
import TicketTypesManager from '../containers/ticketMgmtPortal/TicketTypesManager';
import TicketTypes from '../containers/ticketMgmtPortal/TicketTypes';
import TagsManagement from '../containers/ticketMgmtPortal/manage-tags/TagsManagement';
import CustomFieldManagement from '../containers/ticketMgmtPortal/manage-custom-fields/CustomFieldManagement';
import ExchangeAssigneesManagement from '../containers/ticketMgmtPortal/manage-assignees/ExchangeAssigneesManagment';
import RIDPContainer from '../modules/ridp/index';
import CapApplicationsContainer from '../modules/members/applications';
import AdminSerffStatusContainer from '../modules/admin/serff';
import BrokerTicketListContainer from '../containers/broker/ticket/BrokerTicketListContainer';
import TicketSummaryContainer from '../containers/individualPortal/ticket/TicketSummaryContainer';
import GetStarted from '../modules/ridp/components/Steps/GetStarted';
import ContactInformation from '../modules/ridp/components/Steps/ContactInformation';
import IdentityQuestions from '../modules/ridp/components/Steps/IdentityQuestions';
import CallExperian from '../modules/ridp/components/Steps/CallExperian';
import ManualVerification from '../modules/ridp/components/Steps/ManualVerification';
import Finish from '../modules/ridp/components/Steps/Finish';
import UploadDocumentProcess from '../modules/ridp/components/Steps/UploadDocumentProcess';
import EnrollmentHistory from '../modules/members/enrollments/EnrollmentListing/EnrollmentHistory';
import EnrollmentPremiumContainer from '../modules/members/enrollments/EnrollmentPremium/EnrollmentPremiumContainer';
import Participation from '../containers/BrokerConnectContainer/Participation';
import Availability from '../containers/BrokerConnectContainer/Availability';
import DashboardContainer from '../containers/individualPortal/dashboard/DashboardContainer';
import TobaccoExemptContainer from '../modules/tobacco-exempt';
import ApplicationsContainer from '../containers/individualPortal/applications/ApplicationsContainer';
import EligibilityResultsContainer from '../containers/individualPortal/eligibilityResults/EligibilityResultsContainer';
import CustomGroupingContainer from '../modules/custom-grouping';
import MyEnrollmentHistoryContainer from '../containers/individualPortal/myEnrollment/MyEnrollmentHistoryContainer';
import TicketContainer from '../containers/individualPortal/ticket/TicketContainer';
import MultiHouseholdDetails from '../containers/individualPortal/multiHouseholdDetails/index';
import ManageWorkgroupContainer from '../modules/manageworkgroup/ManageWorkgroupContainer';
import { ResetPasswordContainer, ResetPasswordForm, ResetPasswordSuccess } from '../modules/resetPassword';
import AdminProvision from '../modules/admin/provision/index';

import ArchiveHouseholdContainer from '../modules/archive-household';
import { IssuerEdiTestingContainer, IssuerEdiTestingDetailsContainer } from '../modules/issuer-edi-testing';
import { SecureInboxContainer } from '../modules/secure-inbox';
// types

import type { STATES_TYPE } from '../constants/configs/states';

// Providers

import { getRouteAndState } from '../components/anonymous/home/utils';
import ApplicationEligibility from '../modules/crm-application-eligibility/ApplicationEligibility';
import VerificationsContainer from '../modules/dmi-verifications';
import AddMemberContainer from '../modules/members/add-member/AddMemberContainer';
import EnrollmentContainer from '../modules/members/enrollments/EnrollmentContainer';
import AccountActivityContainer from '../modules/members/account-activity/AccountActivityContainer';
import LoadPlans from '../modules/plans/load-plans/LoadPlans';
import LoadDrugs from '../modules/plans/load-drugs/LoadDrugs';
import { TicketListContainer } from '../modules/cap/TicketList/TicketListContainer';
import MemberApplicationsListContainer from '../modules/members/applications/appContainer/MemberApplicationsListContainer';
import AdminSerffPlansLoadStatusContainer from '../modules/admin/serff/AdminSerffPlansLoadStatus';
import AdminSerffTransferStatusContainer from '../modules/admin/serff/AdminSerffTransferStatus';
import ManageDrugsContainer from '../modules/admin/manage-drugs';
import SecurityQuestionContainer from '../modules/referral/dynamicSecurityQuestions';
import PlanSummaryContainer from '../containers/individualPortal/planSummary/PlanSummaryContainer';
import ManageWorkgroupTagContainer from '../containers/ticketMgmtPortal/manageWorkgroupTags/ManageWorkgroupTagContainer';

interface Props {
    stateCode: STATES_TYPE;
    ticketEnabled: boolean;
    leadPresent: string | null;
    userData: {
        role: string;
        activeModuleName: string;
    };
}

function AppRoutes(props: Props) {
    const {
        routes: {
            ACCESS_CODE,
            ARCHIVE_HOUSEHOLD,
            BROKER_CONNECT,
            BROKER_CONNECT_PARTICIPATION,
            BROKER_CONNECT_AVAILABILITY,
            COMMUNICATION_PREFERENCES_AC,
            CRM_APPLICATION_ELIGIBILITY,
            CRM_SEARCH,
            DELEGATION_OTHER,
            EDE_ID_PROOFING,
            EDE_SEARCH_PAGE,
            MEMBER_ENROLLMENT,
            MEMBER_ENROLLMENT_LIST,
            MEMBER_ENROLLMENT_LIST_PREMIUM_HISTORY,
            MEMBER_ENROLLMENT_PREMIUM_HISTORY,
            ENTITY_TRANSFER_BOB,
            ENTITY_EXPORT_BOB,
            FALLBACK,
            FORGOT_EMAIL,
            HOME,
            INDPORTAL,
            APPLICATIONS,
            COMMUNICATION_PREFERENCES,
            DELEGATION_CONSUMER,
            ELIGIBILITY_RESULTS,
            MULTI_HOUSEHOLD_DETAILS,
            TICKET_CONSUMER,
            TICKET_CONSUMER_DETAIL,
            CUSTOM_GROUPING,
            MY_ENROLLMENT_HISTORY,
            TOBACCO_EXEMPT,
            ISSUER_EDI_TESTING,
            ISSUER_EDI_TESTING_TEST_DETAILS,
            LCE,
            NEW_INBOX_HOME,
            PRE_OEP_LANDING,
            REPORT_DUEDATE_CHARTS,
            RIDP,
            RIDP_CONTACT_INFO,
            RIDP_IDENTITY_QUESTION,
            RIDP_CALL_EXPERIAN,
            RIDP_UPLOAD_DOCUMENT,
            RIDP_VERIFICATION_REJECTED,
            RIDP_TECHNICAL_ERROR,
            RIDP_VERIFICATION_COMPLETE,
            RIDP_UPLOAD_DOCUMENT_PROCESS,
            SECURE_INBOX,
            ACCOUNT_ACTIVITY,
            SSAP,
            TICKET_BROKER,
            TICKET_BROKER_DETAIL,
            TICKET_MGMT,
            TICKET_MGMT_TYPES,
            TICKET_SEND_MESSAGE,
            TICKET_MGMT_EXCHANGE_ASSIGNEES,
            TICKET_MGMT_MANAGE_TAGS,
            TICKET_MGMT_MANAGE_CUSTOM_FIELDS,
            TICKET_MGMT_WORKGROUP_TAG_EDIT,
            TOBACCO,
            VERIFICATION_DOCUMENTS,
            ADD_MEMBER,
            DMI_VERIFICATIONS,
            CAP_APPLICATIONS,
            ADMIN_SERFF,
            ADMIN_SERFF_PLANS_LOAD_STATUS,
            ADMIN_SERFF_TRANSFER_STATUS,
            LOAD_PLANS,
            LOAD_DRUGS,
            TICKET_LIST,
            CAP_APPLICATIONS_SSAP_SUMMARY,
            MANAGE_DRUGS,
            DYNAMIC_SECURITY_QUESTIONS,
            PLAN_SUMMARY,
            TICKET_MGMT_MANAGE_WORKGROUP,
            RESET_PASSWORD,
            RESET_PASSWORD_SUCCESS,
            ADMIN_PROVISION,
        },
    } = configs;

    const { stateCode, ticketEnabled, userData, leadPresent } = props;
    
    const isNevada = stateCode === states.NEVADA;
    const isNewJersey = stateCode === states.NEW_JERSEY;
    const isIdaho = stateCode === states.IDAHO;
    const isPennie = stateCode === states.PENNSYLVANIA;
    const isVirginia = stateCode === states.VIRGINIA;
    const isGeorgia = stateCode === states.GEORGIA;
    const isNewMexico = stateCode === states.NEW_MEXICO;
    const isBrokerConnectAllowed = isNevada || isPennie || isVirginia || isGeorgia || isNewMexico;
    const isPreOEPLandingAllowed = isGeorgia || isVirginia;
    const canViewMemberPortal = userData?.activeModuleName.toUpperCase() === ALL_USER_ROLES.INDIVIDUAL;

    const updatedPath = getRouteAndState(leadPresent, null, stateCode);
    const REDIRECTED_LANDING_PAGE_PATH = updatedPath.route;
    
    const isForAdminOrOperations = [ALL_USER_ROLES.ADMIN, ALL_USER_ROLES.OPERATIONS].includes(userData?.activeModuleName?.toUpperCase());

    const individualPortalRoutes = [
        { path: INDPORTAL, element: <DashboardContainer />, isAllowed: canViewMemberPortal },
        { path: TOBACCO_EXEMPT, element: <RouteRenderEnvironment component={TobaccoExemptContainer} />, isAllowed: canViewMemberPortal },
        { path: APPLICATIONS, element: <RouteRenderEnvironment component={ApplicationsContainer} />, isAllowed: canViewMemberPortal },
        { path: COMMUNICATION_PREFERENCES, element: <RouteRenderEnvironment component={CommunicationPreferencesContainer} />, isAllowed: canViewMemberPortal },
        { path: DELEGATION_CONSUMER, element: <RouteRenderEnvironment component={DelegationContainer} />, isAllowed: canViewMemberPortal },
        { path: ELIGIBILITY_RESULTS, element: <RouteRenderEnvironment component={EligibilityResultsContainer} />, isAllowed: canViewMemberPortal },
        { path: MULTI_HOUSEHOLD_DETAILS, element: <MultiHouseholdDetails />, isAllowed: canViewMemberPortal },
        { path: CUSTOM_GROUPING, element: <CustomGroupingContainer />, isAllowed: canViewMemberPortal },
        { path: MY_ENROLLMENT_HISTORY, element: <MyEnrollmentHistoryContainer />, isAllowed: canViewMemberPortal },
        { path: TICKET_CONSUMER, element: <TicketContainer />, isAllowed: ticketEnabled },
        { path: TICKET_CONSUMER_DETAIL, element: <TicketSummaryContainer />, isAllowed: ticketEnabled },
        {
            path: PLAN_SUMMARY,
            element: <RouteRenderEnvironment component={PlanSummaryContainer} />,
            isAllowed: canViewMemberPortal,
        },
    ];

    return (
        <Routes>
            <Route path={VERIFICATION_DOCUMENTS} element={<DocumentsVerificationContainer />} />
            <Route path={ARCHIVE_HOUSEHOLD} element={<ArchiveHouseholdContainer />} />
            <Route
                path={HOME}
                element={
                    <PrivateRoute
                        path={HOME}
                        isAllowed={
                            isGeorgia || isIdaho || isNevada || isNewJersey || isPennie || isVirginia || isNewMexico
                        }
                        element={<HomeContainer />}
                        redirectPath={REDIRECTED_LANDING_PAGE_PATH}
                    />
                }
            />
            <Route path={COMMUNICATION_PREFERENCES_AC} element={<CommunicationPreferencesContainer />} />
            <Route path={ACCESS_CODE} element={<AccessCodeContainer />} />
            <Route path={FALLBACK} element={<FallbackContainer />} />
            <Route path={LCE} element={<LceContainer />} />
            <Route path={SSAP} element={<SsapContainer />} />
            <Route path={DELEGATION_OTHER} element={<DelegationContainer />} />
            <Route path={TOBACCO} element={<TobaccoContainer />} />
            <Route path={CRM_SEARCH} element={<CrmContainer />} />
            <Route
                path={EDE_ID_PROOFING}
                element={
                    <RouteRenderEnvironment component={EDESearchAndSelfDesignationContainer} />
                }
            >
                <Route path={EDE_ID_PROOFING} element={<IdProofingContainer />} />
                <Route path={EDE_SEARCH_PAGE} element={<EDESearchContainer />} />
            </Route>
            <Route path={FORGOT_EMAIL} element={<ForgotEmailContainer />} />
            <Route
                path={TICKET_BROKER}
                element={<PrivateRoute path={TICKET_BROKER} isAllowed={ticketEnabled} element={<BrokerContainer />} />}
            >
                <Route
                    path={TICKET_BROKER}
                    element={
                        <PrivateRoute
                            path={TICKET_BROKER}
                            isAllowed={ticketEnabled}
                            element={<BrokerTicketListContainer />}
                        />
                    }
                />
                <Route path={TICKET_BROKER_DETAIL} element={<TicketSummaryContainer />} />
            </Route>
            <Route path={ENTITY_EXPORT_BOB} element={<EntityExportContainer />} />
            <Route path={ENTITY_TRANSFER_BOB} element={<EntityTransportBOBContainer />} />
            <Route path={NEW_INBOX_HOME} element={<RouteRenderEnvironment component={SecureInbox} />} />
            <Route path={TICKET_MGMT} element={<TicketMgmtContainer />}>
                <Route path={TICKET_SEND_MESSAGE} element={<TicketSendMessageContainer />} />
                <Route path={TICKET_MGMT_EXCHANGE_ASSIGNEES} element={<ExchangeAssigneesManagement />} />
                <Route
                    path={TICKET_MGMT_MANAGE_TAGS}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_MANAGE_TAGS}
                            isAllowed={isForAdminOrOperations}
                            element={<TagsManagement/>}
                        />
                    }
                />
                <Route
                    path={TICKET_MGMT_TYPES}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_TYPES}
                            isAllowed={isForAdminOrOperations}
                            element={<TicketTypesManager/>}
                        />
                    }
                >
                    <Route path={`${TICKET_MGMT_TYPES}/:ticketCategory/:ticketCategorySpanishName?`} element={<TicketTypes/>}/>
                </Route>
                <Route
                    path={TICKET_MGMT_MANAGE_CUSTOM_FIELDS}
                    element={
                        <PrivateRoute
                            path={TICKET_MGMT_MANAGE_CUSTOM_FIELDS}
                            isAllowed={isForAdminOrOperations}
                            element={<CustomFieldManagement />}
                        />
                    }
                />
                <Route path={ TICKET_MGMT_WORKGROUP_TAG_EDIT } element={ <ManageWorkgroupTagContainer/> } />
                <Route path={TICKET_MGMT_MANAGE_WORKGROUP} element={<ManageWorkgroupContainer />} />
            </Route>
            <Route path={SECURE_INBOX} element={<SecureInboxContainer />} />
            <Route path={ACCOUNT_ACTIVITY} element={<AccountActivityContainer />} />
            <Route
                path={BROKER_CONNECT}
                element={
                    <PrivateRoute
                        path={BROKER_CONNECT}
                        isAllowed={isBrokerConnectAllowed}
                        element={<BrokerConnectContainer />}
                    />
                }
            >
                <Route
                    path={BROKER_CONNECT_PARTICIPATION}
                    element={
                        <PrivateRoute
                            path={BROKER_CONNECT_PARTICIPATION}
                            isAllowed={isBrokerConnectAllowed}
                            element={<Participation />}
                        />
                    }
                />
                <Route
                    path={BROKER_CONNECT_AVAILABILITY}
                    element={
                        <PrivateRoute
                            path={BROKER_CONNECT_AVAILABILITY}
                            isAllowed={isBrokerConnectAllowed}
                            element={<Availability />}
                        />
                    }
                />
            </Route>
            <Route path={ISSUER_EDI_TESTING} element={<IssuerEdiTestingContainer />} />
            <Route path={ISSUER_EDI_TESTING_TEST_DETAILS} element={<IssuerEdiTestingDetailsContainer />} />
            <Route
                path={INDPORTAL}
                element={
                    <PrivateRoute
                        path={INDPORTAL}
                        isAllowed={canViewMemberPortal}
                        element={<IndividualPortalContainer />}
                        redirectPath={FALLBACK}
                    />
                }
            >
                {individualPortalRoutes.map(({ path, element, isAllowed }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <PrivateRoute path={path} isAllowed={isAllowed} element={element} redirectPath={FALLBACK} />
                        }
                    />
                ))}
            </Route>
            <Route path={CRM_APPLICATION_ELIGIBILITY} element={<ApplicationEligibility />} />
            <Route
                path={PRE_OEP_LANDING}
                element={
                    <PrivateRoute
                        path={PRE_OEP_LANDING}
                        isAllowed={isPreOEPLandingAllowed}
                        element={<PreOEPLandingContainer />}
                    />
                }
            />
            <Route path={REPORT_DUEDATE_CHARTS} element={<UpcomingDueDateContainer />} />
            <Route path={ADD_MEMBER} element={<AddMemberContainer />} />
            <Route path={RIDP} element={<RouteRenderEnvironment component={RIDPContainer} />}>
                <Route path={RIDP} element={<GetStarted />} />
                <Route path={RIDP_CONTACT_INFO} element={<ContactInformation />} />
                <Route path={RIDP_IDENTITY_QUESTION} element={<IdentityQuestions />} />
                <Route path={RIDP_CALL_EXPERIAN} element={<CallExperian />} />
                <Route path={RIDP_UPLOAD_DOCUMENT} element={<ManualVerification />} />
                <Route path={RIDP_VERIFICATION_REJECTED} element={<ManualVerification />} />
                <Route path={RIDP_TECHNICAL_ERROR} element={<Finish />} />
                <Route path={RIDP_VERIFICATION_COMPLETE} element={<Finish />} />
                <Route path={RIDP_UPLOAD_DOCUMENT_PROCESS} element={<UploadDocumentProcess />} />
            </Route>
            <Route path={DMI_VERIFICATIONS} element={<VerificationsContainer />} />
            <Route path={MEMBER_ENROLLMENT} element={<EnrollmentContainer />}>
                <Route path={MEMBER_ENROLLMENT_LIST} element={<EnrollmentHistory />} />
                <Route path={MEMBER_ENROLLMENT_LIST_PREMIUM_HISTORY} element={<EnrollmentPremiumContainer />} />
                <Route path={MEMBER_ENROLLMENT_PREMIUM_HISTORY} element={<EnrollmentPremiumContainer />} />
            </Route>
            <Route path={CAP_APPLICATIONS} element={<CapApplicationsContainer />}>
                <Route path={CAP_APPLICATIONS} element={<MemberApplicationsListContainer />}/>
                <Route path={CAP_APPLICATIONS_SSAP_SUMMARY} element={<SsapContainer />} />
            </Route>
            <Route path={RESET_PASSWORD} element={<ResetPasswordContainer />}>
                <Route path={RESET_PASSWORD} element={<ResetPasswordForm />}></Route>
                <Route path={RESET_PASSWORD_SUCCESS} element={<ResetPasswordSuccess />}></Route>
            </Route>
            <Route path={ADMIN_SERFF} element={<AdminSerffStatusContainer />}>
                <Route index element={<Navigate to={ADMIN_SERFF_PLANS_LOAD_STATUS} replace />} />
                <Route path={ADMIN_SERFF_PLANS_LOAD_STATUS} element={<AdminSerffPlansLoadStatusContainer />} />
                <Route path={ADMIN_SERFF_TRANSFER_STATUS} element={<AdminSerffTransferStatusContainer />} />
            </Route>
            <Route path={ADMIN_PROVISION} element={<AdminProvision />} />
            <Route path={MANAGE_DRUGS} element={<ManageDrugsContainer />}  />
            <Route path={TICKET_LIST} element={<RouteRenderEnvironment component={TicketListContainer} />} />
            <Route path={LOAD_PLANS} element={<RouteRenderEnvironment component={LoadPlans} />} />
            <Route path={LOAD_DRUGS} element={<LoadDrugs/>} />
            <Route path={DYNAMIC_SECURITY_QUESTIONS} element={<SecurityQuestionContainer />}  />
        </Routes>
    );
}

export default AppRoutes;